import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "@emotion/styled";
import { Tooltip } from "antd";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { POST_GOOGLE_SIGNIN_URL } from "@libs/apiUrl";
import { useNavigate } from "react-router-dom";

const GoogleButton = styled.button({
  display: "flex",
  width: "5.2rem",
  height: "5.2rem",
  borderRadius: "50%",
  overflow: "hidden",
  border: "1px solid #dadce0",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#ffffff",
  cursor: "pointer",
});

const tooltipStyle = {
  backgroundColor: "#FF4808",
  borderRadius: "2px ",
  padding: "0.2rem 0.8rem",
  height: "auto",
  minHeight: "auto",
  fontSize: "1.2rem",
  fontFamily: "Pretendard",
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: () => void;
          renderButton: (element: HTMLElement, config: Record<string, unknown>) => void;
        };
      };
    };
  }
}

const GoogleLoginButton = ({
  recentLogin,
}: {
  recentLogin: string | null;
}) => {
  const navigate = useNavigate();
  const [gsiReady, setGsiReady] = useState(false);
  const scriptLoaded = useRef(false);
  const googleButtonContainerRef = useRef<HTMLDivElement | null>(null);

  const mutateRef = useRef<((token: string) => void) | null>(null);

  const { mutate } = useMutation({
    mutationFn: async (id_token: string) => {
      const { data, headers } = await axios.post(
        POST_GOOGLE_SIGNIN_URL,
        { id_token, role: "BRAND" },
        { withCredentials: true },
      );
      return { data, headers };
    },
    onSuccess: (res) => {
      const { data } = res;
      const responseData = data?.data;

      if (responseData?.access_token) {
        // 로그인 성공 (기가입 또는 자동 회원 생성)
        localStorage.setItem("accessToken", responseData.access_token);
        localStorage.setItem("loginType", "GOOGLE");
        localStorage.setItem("isLoggedIn", "true");
        navigate("/");
      } else if (responseData?.link_token) {
        // 계정 연결 필요 (이미 같은 이메일로 가입된 계정 존재)
        alert("이미 해당 이메일로 가입된 계정이 있습니다. 기존 계정으로 로그인해주세요.");
      }
    },
    onError: (error) => {
      console.error("Google 로그인 실패:", error);
    },
  });

  // mutate를 ref로 유지하여 SDK callback에서 최신 참조 사용
  mutateRef.current = mutate;

  // Google GSI 스크립트 로드
  useEffect(() => {
    if (scriptLoaded.current) return;
    scriptLoaded.current = true;

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => setGsiReady(true);
    document.head.appendChild(script);
  }, []);

  // SDK 준비되면 숨겨진 Google 버튼 렌더
  const handleCredential = useCallback((response: any) => {
    if (response.credential) {
      mutateRef.current?.(response.credential);
    }
  }, []);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const container = googleButtonContainerRef.current;

    if (!clientId || !gsiReady || !container || !window.google?.accounts?.id) {
      return;
    }

    container.innerHTML = "";

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredential,
    });

    window.google.accounts.id.renderButton(container, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "signin_with",
      shape: "rectangular",
      width: 320,
    });
  }, [gsiReady, handleCredential]);

  const handleGoogleLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!gsiReady || !window.google) return;

    const googleButton =
      googleButtonContainerRef.current?.querySelector<HTMLElement>(
        'div[role="button"]',
      ) ||
      googleButtonContainerRef.current?.querySelector<HTMLElement>(
        "[tabindex]",
      );

    if (!googleButton) {
      console.error("Google Sign-In button not ready");
      return;
    }

    googleButton.dispatchEvent(
      new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: true,
      }),
    );
  };

  return (
    <>
      {/* 숨겨진 Google 렌더 버튼 컨테이너 */}
      <div
        aria-hidden="true"
        style={{ position: "absolute", left: -9999, top: 0, opacity: 0 }}
      >
        <div ref={googleButtonContainerRef} />
      </div>

      <Tooltip
        title={recentLogin === "GOOGLE" ? "최근 로그인" : ""}
        placement="top"
        open={recentLogin === "GOOGLE"}
        color="#FF4808"
        overlayInnerStyle={tooltipStyle}>
        <GoogleButton type="button" onClick={handleGoogleLogin}>
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M21.8055 10.0415H21V10H12V14H17.6515C16.827 16.3285 14.6115 18 12 18C8.6865 18 6 15.3135 6 12C6 8.6865 8.6865 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C6.4775 2 2 6.4775 2 12C2 17.5225 6.4775 22 12 22C17.5225 22 22 17.5225 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z"
              fill="#FFC107"
            />
            <path
              d="M3.15295 7.3455L6.43845 9.755C7.32745 7.554 9.48045 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C8.15895 2 4.82795 4.1685 3.15295 7.3455Z"
              fill="#FF3D00"
            />
            <path
              d="M12 22C14.583 22 16.93 21.0115 18.7045 19.404L15.6095 16.785C14.5718 17.5742 13.3038 18.001 12 18C9.39897 18 7.19047 16.3415 6.35847 14.027L3.09747 16.5395C4.75247 19.778 8.11347 22 12 22Z"
              fill="#4CAF50"
            />
            <path
              d="M21.8055 10.0415H21V10H12V14H17.6515C17.2571 15.1082 16.5467 16.0766 15.608 16.7855L15.6095 16.7845L18.7045 19.4035C18.4855 19.6025 22 17 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z"
              fill="#1976D2"
            />
          </svg>
        </GoogleButton>
      </Tooltip>
    </>
  );
};

export default GoogleLoginButton;
